import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@fc/utilities';
import PaginatedList from '@fc/pagination';
import { FCEvents } from '@fc/events';

const FIT_RESULTS_TAG = 'search-results';
const FIT_GRID_ATTRIBUTE = 'product-grid-view';
const FIT_LAYOUT_FALLBACK = 'default';
const FIT_TRANSITION_SCOPE = ['product-grid'];
const FIT_VIEW_STORAGE_PREFIX = 'product-grid-view-';
const FIT_VIEWPORT_SELECTORS = {
  desktop: '[data-grid-layout="desktop-default-option"]',
  mobile: '[data-grid-layout="mobile-option"]',
};
const FIT_IMAGE_TOGGLE_KEY = 'fit-image-view-alt';
const FIT_MEDIA_CARD_SELECTOR = 'product-tile, product-card';
const FIT_TOGGLE_INPUT_SELECTOR = 'input[name="fit_image_toggle"]';
const FIT_CARD_LINK_SELECTOR = '[data-ref="cardGalleryLink"][data-alt-image-url]';
const FIT_ALT_PREFETCH_LIMIT = 4;
const FIT_IMAGE_MUTATION_ATTRIBUTES = [
  'src',
  'srcset',
  'sizes',
  'data-primary-image-url',
  'data-primary-image-srcset',
  'data-primary-image-max-url',
  'data-primary-image-alt',
  'data-primary-image-width',
  'data-primary-image-height',
  'data-alt-image-url',
  'data-alt-image-srcset',
  'data-alt-image-max-url',
  'data-alt-image-alt',
  'data-alt-image-width',
  'data-alt-image-height',
  'data-image-sizes',
];

// Paginated results with grid/list view switching and viewport persistence
export default class ResultsList extends PaginatedList {
  #abortController = null;
  #cardObserver = null;
  #imageApplyToken = 0;

  #getViewportMode() {
    return mediaQueryLarge?.matches ? 'desktop' : 'mobile';
  }

  #getStorageKey() {
    return `${FIT_VIEW_STORAGE_PREFIX}${this.#getViewportMode()}`;
  }

  #setGridView(value) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return false;
    grid.setAttribute(FIT_GRID_ATTRIBUTE, value);
    return true;
  }

  #persistLayoutPreference(value) {
    requestIdleCallback(() => {
      try {
        sessionStorage.setItem(this.#getStorageKey(), value);
      } catch {
        // Session storage is best-effort only.
      }
    });
  }

  #resolveViewportInput(matchesDesktop) {
    const selector = matchesDesktop ? FIT_VIEWPORT_SELECTORS.desktop : FIT_VIEWPORT_SELECTORS.mobile;
    const input = this.querySelector(selector);
    if (input instanceof HTMLInputElement) return input;
    return null;
  }

  async #runViewTransition(value) {
    if (typeof startViewTransition === 'function') {
      await startViewTransition(() => this.#setGridView(value), FIT_TRANSITION_SCOPE);
      return;
    }
    this.#setGridView(value);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (mediaQueryLarge?.addEventListener) {
      mediaQueryLarge.addEventListener('change', this.#onBreakpoint, {
        signal: this.#abortController.signal,
        passive: true,
      });
    }

    this.setAttribute('initialized', '');
    this.#bindImageToggle();
    this.#restoreImageToggle();
    this.#observeNewCards();
    this.#prefetchVisibleAlternateImages();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cardObserver?.disconnect();
    this.#cardObserver = null;
    this.#imageApplyToken += 1;
  }

  onMorphSync() {
    super.onMorphSync?.();
    this.#cardObserver?.disconnect();
    this.#cardObserver = null;
    this.#restoreImageToggle();
    this.#observeNewCards();
    this.#prefetchVisibleAlternateImages();
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const nextLayout = target.value;
    if (!nextLayout || typeof nextLayout !== 'string') return;
    this.#runViewTransition(nextLayout);
    this.#persistLayoutPreference(nextLayout);
  }

  #onBreakpoint = (e) => {
    const viewportInput = this.#resolveViewportInput(e.matches);
    if (!(viewportInput instanceof HTMLInputElement)) return;
    viewportInput.checked = true;
    this.#setGridView(FIT_LAYOUT_FALLBACK);
  };

  // ── Image toggle ──────────────────────────────────────────────

  #bindImageToggle() {
    this.addEventListener('change', (e) => {
      if (!(e.target instanceof HTMLInputElement)) return;
      if (e.target.name === 'fit_image_toggle') {
        this.toggleImageView({ target: e.target });
      }
    }, { signal: this.#abortController.signal, passive: true });

    this.addEventListener(FCEvents.variantChange, this.#onVariantChange, {
      signal: this.#abortController.signal,
      capture: true,
    });
  }

  toggleImageView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const showSecond = target.checked;
    this.#syncToggleInputs(showSecond);
    this.#persistImageToggle(showSecond);
    this.#applyImageView(showSecond, { visibleFirst: true });
  }

  #hasImageToggleControls() {
    return this.querySelector(FIT_TOGGLE_INPUT_SELECTOR) instanceof HTMLInputElement;
  }

  #shouldUseAlternateImages() {
    return this.dataset.fitBaseSlide === '1' && this.#hasImageToggleControls();
  }

  #hasStoredAlternatePreference() {
    if (!this.#hasImageToggleControls()) return false;

    try {
      return localStorage.getItem(FIT_IMAGE_TOGGLE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #collectImageLinks(scope = this) {
    const roots =
      Array.isArray(scope) || scope instanceof NodeList || scope instanceof HTMLCollection ? Array.from(scope) : [scope];
    const links = new Set();

    for (const root of roots) {
      if (!(root instanceof Element || root instanceof DocumentFragment)) continue;

      if (root instanceof Element && root.matches(FIT_CARD_LINK_SELECTOR)) {
        links.add(root);
      }

      for (const link of root.querySelectorAll(FIT_CARD_LINK_SELECTOR)) {
        links.add(link);
      }
    }

    return Array.from(links);
  }

  #collectMediaCards(scope = this) {
    const roots =
      Array.isArray(scope) || scope instanceof NodeList || scope instanceof HTMLCollection ? Array.from(scope) : [scope];
    const cards = new Set();

    for (const root of roots) {
      if (!(root instanceof Element || root instanceof DocumentFragment)) continue;

      if (root instanceof Element && root.matches(FIT_MEDIA_CARD_SELECTOR) && typeof root.setMediaMode === 'function') {
        cards.add(root);
      }

      for (const card of root.querySelectorAll(FIT_MEDIA_CARD_SELECTOR)) {
        if (typeof card.setMediaMode === 'function') cards.add(card);
      }
    }

    return Array.from(cards);
  }

  #isVisibleLink(link) {
    const rect = link.getBoundingClientRect();
    return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  #applyImageView(showSecond, { scope = this, visibleFirst = false, invalidatePending = true } = {}) {
    if (showSecond && !this.#hasImageToggleControls()) {
      this.dataset.fitBaseSlide = '0';
      this.#imageApplyToken += 1;
      return;
    }

    const nextBaseSlide = showSecond ? '1' : '0';
    const shouldInvalidatePending = invalidatePending || this.dataset.fitBaseSlide !== nextBaseSlide;
    this.dataset.fitBaseSlide = nextBaseSlide;
    const cards = this.#collectMediaCards(scope);
    const cardSet = new Set(cards);
    const links = this.#collectImageLinks(scope).filter((link) => !cardSet.has(link.closest(FIT_MEDIA_CARD_SELECTOR)));
    const token = shouldInvalidatePending ? ++this.#imageApplyToken : this.#imageApplyToken;

    if (!visibleFirst) {
      this.#applyImageCards(cards, showSecond);
      this.#applyImageLinks(links, showSecond, token);
      return;
    }

    const visibleCards = [];
    const deferredCards = [];
    const visibleLinks = [];
    const deferredLinks = [];

    for (const card of cards) {
      (this.#isVisibleLink(card) ? visibleCards : deferredCards).push(card);
    }

    for (const link of links) {
      (this.#isVisibleLink(link) ? visibleLinks : deferredLinks).push(link);
    }

    this.#applyImageCards(visibleCards, showSecond);
    this.#applyImageLinks(visibleLinks, showSecond, token);

    if (deferredCards.length > 0 || deferredLinks.length > 0) {
      requestIdleCallback(() => {
        this.#applyImageCards(deferredCards, showSecond);
        this.#applyImageLinks(deferredLinks, showSecond, token);
      });
    }
  }

  #applyImageCards(cards, showSecond) {
    const mode = showSecond ? 'alternate' : 'primary';

    for (const card of cards) {
      if (typeof card.setMediaMode === 'function') card.setMediaMode(mode);
    }
  }

  #applyImageLinks(links, showSecond, token = this.#imageApplyToken) {
    if (token !== this.#imageApplyToken) return;

    for (const link of links) {
      if (!(link instanceof HTMLElement)) continue;
      const image = link.querySelector('img');
      if (image instanceof HTMLImageElement) this.#setImageSource(image, link, showSecond);
    }
  }

  #onVariantChange = (event) => {
    if (!this.#shouldUseAlternateImages()) return;
    const target = event.target instanceof Element ? event.target : null;
    const changedCard = target?.closest('product-tile, product-card, [data-ref="cards[]"]') ?? this;

    requestAnimationFrame(() => {
      if (!this.#shouldUseAlternateImages()) return;
      this.#syncToggleInputs(true);
      this.#applyImageView(true, { scope: changedCard, invalidatePending: false });
    });
  };

  #setAttributeIfChanged(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  #removeAttributeIfPresent(element, name) {
    if (element.hasAttribute(name)) element.removeAttribute(name);
  }

  #setImageSource(img, link, showSecond) {
    const prefix = showSecond ? 'alt' : 'primary';
    const url = link.dataset[`${prefix}ImageUrl`];
    if (!url) return;

    const srcset = link.dataset[`${prefix}ImageSrcset`];
    const sizes = link.dataset.imageSizes;
    const alt = link.dataset[`${prefix}ImageAlt`];
    const width = link.dataset[`${prefix}ImageWidth`];
    const height = link.dataset[`${prefix}ImageHeight`];
    const maxResolution = link.dataset[`${prefix}ImageMaxUrl`] || url;

    if (sizes) this.#setAttributeIfChanged(img, 'sizes', sizes);
    if (srcset) {
      this.#setAttributeIfChanged(img, 'srcset', srcset);
    } else {
      this.#removeAttributeIfPresent(img, 'srcset');
      if (img.srcset !== '') img.srcset = '';
    }
    if (alt !== undefined) this.#setAttributeIfChanged(img, 'alt', alt);
    if (width) this.#setAttributeIfChanged(img, 'width', width);
    if (height) this.#setAttributeIfChanged(img, 'height', height);
    if (img.getAttribute('src') !== url) {
      img.setAttribute('src', url);
      img.src = url;
    }

    this.#setAttributeIfChanged(img, 'data-max-resolution', maxResolution);
    this.#setAttributeIfChanged(img, 'data_max_resolution', maxResolution);
  }

  #persistImageToggle(showSecond) {
    try {
      localStorage.setItem(FIT_IMAGE_TOGGLE_KEY, showSecond ? '1' : '0');
    } catch {
      // localStorage is best-effort only.
    }
  }

  #restoreImageToggle() {
    try {
      if (!this.#hasImageToggleControls()) {
        this.dataset.fitBaseSlide = '0';
        this.#imageApplyToken += 1;
        return;
      }

      if (!this.#hasStoredAlternatePreference()) return;
      this.#syncToggleInputs(true);
      this.#applyImageView(true, { visibleFirst: true });
    } catch {
      // localStorage is best-effort only.
    }
  }

  #syncToggleInputs(checked) {
    const inputs = this.querySelectorAll(FIT_TOGGLE_INPUT_SELECTOR);
    for (const input of inputs) {
      if (input instanceof HTMLInputElement) input.checked = checked;
    }
  }

  #observeNewCards() {
    this.#cardObserver?.disconnect();

    this.#cardObserver = new MutationObserver((mutations) => {
      if (!this.#shouldUseAlternateImages() && !this.#hasStoredAlternatePreference()) return;

      const scopes = new Set();
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) scopes.add(node);
        }

        if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          const target = mutation.target;
          scopes.add(target.closest(FIT_CARD_LINK_SELECTOR) ?? target.closest('product-tile, product-card, [data-ref="cards[]"]') ?? target);
        }
      }
      if (scopes.size === 0) return;

      requestAnimationFrame(() => {
        if (!this.#shouldUseAlternateImages() && !this.#hasStoredAlternatePreference()) return;
        this.#syncToggleInputs(true);
        this.#applyImageView(true, { scope: Array.from(scopes), invalidatePending: false });
      });
    });

    this.#cardObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: FIT_IMAGE_MUTATION_ATTRIBUTES,
    });
  }

  #prefetchVisibleAlternateImages() {
    if (!this.#hasImageToggleControls()) return;
    if (navigator.connection?.saveData) return;

    try {
      if (localStorage.getItem(FIT_IMAGE_TOGGLE_KEY) === '1') return;
    } catch {
      // localStorage is best-effort only.
    }

    requestIdleCallback(() => {
      let prefetched = 0;

      for (const link of this.#collectImageLinks(this)) {
        if (prefetched >= FIT_ALT_PREFETCH_LIMIT) return;
        if (!(link instanceof HTMLElement) || link.dataset.altPrefetched === 'true') continue;
        if (!this.#isVisibleLink(link)) continue;

        const url = link.dataset.altImageUrl;
        if (!url) continue;

        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'low';
        if (link.dataset.imageSizes) image.sizes = link.dataset.imageSizes;
        if (link.dataset.altImageSrcset) image.srcset = link.dataset.altImageSrcset;
        image.src = url;
        link.dataset.altPrefetched = 'true';
        prefetched += 1;
      }
    });
  }
}

if (!customElements.get(FIT_RESULTS_TAG)) {
  customElements.define(FIT_RESULTS_TAG, ResultsList);
}
